import { css } from '@emotion/react';
import {
  faEmptySet, faEnvelope, faLockAlt, faShieldCheck,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type FormEvent, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  type DropdownOption, RegularDropdownComponent,
} from '~/_shared/baseComponents/dropdown/regularDropdown.component';
import { TextInputAuthComponent } from '~/_shared/baseComponents/inputs';
import { LinkComponent } from '~/_shared/baseComponents/links';
import { TwoFactorMode } from '~/_shared/constants/twoFactorMode.enum';
import {
  type TranslationFnc, useTranslation,
} from '~/_shared/utils/hooks';
import { EmailSentAlertComponent } from '~/authentication/emailSentAlert.component';
import authenticatorLogo from '../../../../../assets/images/2fa/2falogo.svg';
import {
  authorizationPagesAlertStyle, authorizationPagesLinkStyle,
} from '../../authenticationPages.styles';
import { AuthorizationSubmitButtonComponent } from '../../submitButton/authenticationSubmitButton.component';
import { validate2FaLoginForm } from './loginForm.helpers';
import { UserLoginErrorComponent } from './userLoginError.component';

const authenticatorLogoWrapperStyle = css({
  display: 'flex',
  margin: '64px 0 32px',
  justifyContent: 'center',
});

const labelStyle = css({
  marginBottom: 4,
});

const inputStyle = css({
  margin: '10px 0 15px',
});

const mfaModeDropdownContainerStyle = css({
  marginBottom: 15,
});

const mfaModeDropdownStyle = css({
  width: '100%',
});

const alertStyle = css(authorizationPagesAlertStyle, {
  marginTop: 10,
});

const linksWrapperStyle = css({
  marginTop: 20,
  display: 'flex',
  width: '100%',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
});

const authenicatorLogoStyle = css({
  display: 'block',
  maxWidth: 300,
  width: '120%',
  height: 'auto',
});

const getAuthenticatorModeInfo = (mode: TwoFactorMode, t: TranslationFnc) => {
  switch (mode) {
    case TwoFactorMode.email:
      return {
        name: t('Email'),
        icon: faEnvelope,
      };
    case TwoFactorMode.google:
      return {
        name: t('Google Authenticator'),
        icon: faShieldCheck,
      };
    default:
      return {
        name: 'Unknown 2FA provider', // Twilio exists in Nova.
        icon: faEmptySet,
      };
  }
};

const resendLinkStyle = ({ isDisabled }: { isDisabled: boolean }) => css(authorizationPagesLinkStyle, {
  cursor: isDisabled ? 'not-allowed' : 'default',
});

type Login2FaFormProps = {
  error: string;
  isLoading: boolean;
  mfaMode: TwoFactorMode;
  availableMfaModes: readonly TwoFactorMode[];

  onSubmit: (code: string) => void;
  repeatMFA?: (successCallback?: () => void) => void;
  reset2FaMode: () => void;
  onChangeMfaMode: (mode: TwoFactorMode) => void;
};

export const Login2FaFormComponent: FC<Login2FaFormProps> = (props) => {
  const [code, setCode] = useState('');
  const [t] = useTranslation();
  const [showError, setShowError] = useState(true);
  const [isRepeatSuccess, setIsRepeatSuccess] = useState(false);

  const mfaModeOptions = useMemo<DropdownOption<TwoFactorMode>[]>(() => {
    return props.availableMfaModes.map(mode => ({
      value: mode,
      ...getAuthenticatorModeInfo(mode, t),
    }));
  }, [props.availableMfaModes, t]);

  const onFormSubmit = useCallback((e: FormEvent) => {
    e.preventDefault();
    if (!validate2FaLoginForm(code)) {
      return;
    }
    const onSubmit = props.onSubmit;
    onSubmit(code);
  }, [code, props.onSubmit]);

  const onReset2FaMode = useCallback(() => {
    const reset2FaMode = props.reset2FaMode;
    reset2FaMode();
  }, [props.reset2FaMode]);

  const handleRepeatMFA = useCallback(() => {
    const repeat = props.repeatMFA;

    if (props.mfaMode === TwoFactorMode.email && repeat) {
      repeat();
      setIsRepeatSuccess(true);
    }
  }, [props.mfaMode, props.repeatMFA]);

  const handleEmailSendingOver = useCallback(() => setIsRepeatSuccess(false), []);

  useEffect(() => {
    setShowError(false);
  }, [code]);

  useEffect(() => {
    setShowError(!!props.error);
  }, [props.error]);

  const isFormValid = validate2FaLoginForm(code) && !props.isLoading;

  return (
    <form onSubmit={onFormSubmit} >
      {props.mfaMode === TwoFactorMode.google && (
        <div css={authenticatorLogoWrapperStyle} >
          <img
            css={authenicatorLogoStyle}
            alt="Google Authenticator"
            src={authenticatorLogo}
          />
        </div>
      )}

      <div>
        {(props.availableMfaModes.length > 1) && (
          <div css={mfaModeDropdownContainerStyle}>
            <span css={labelStyle}>{t('Select authentication method')}</span>
            <div css={inputStyle}>
              <RegularDropdownComponent
                css={mfaModeDropdownStyle}
                options={mfaModeOptions}
                value={props.mfaMode}
                onChange={props.onChangeMfaMode}
                isDisabled={props.isLoading}
              />
            </div>
          </div>
        )}

        {props.mfaMode === TwoFactorMode.email ?
          <span css={labelStyle}>{t('Please enter the code we have just sent to your E-mail address')}</span> :
          <span css={labelStyle}>{t('Please enter the one-time code found in your Google Authenticator')}</span>}

        <div css={inputStyle}>
          <TextInputAuthComponent
            onChange={setCode}
            value={code}
            placeholder={t('Enter code here')}
            icon={props.mfaMode === 'email' ? faEnvelope : faLockAlt}
            isDisabled={props.isLoading}
          />
        </div>

        <AuthorizationSubmitButtonComponent
          isLoading={props.isLoading}
          isDisabled={!isFormValid}
        >
          {t('Submit')}
        </AuthorizationSubmitButtonComponent>

        {showError && (
          <UserLoginErrorComponent code={props.error} />
        )}

        <div css={linksWrapperStyle}>
          <LinkComponent
            css={authorizationPagesLinkStyle}
            onClick={onReset2FaMode}
            path=""
          >
            {t('Go to Login Page')}
          </LinkComponent>
          {props.repeatMFA && (
            <LinkComponent
              css={resendLinkStyle({ isDisabled: isRepeatSuccess })}
              disabled={isRepeatSuccess}
              onClick={handleRepeatMFA}
              path={null}
            >
              {props.mfaMode === TwoFactorMode.email ? t('Resend Email') : undefined}
            </LinkComponent>
          )}
        </div>
        {isRepeatSuccess && (
          <div css={alertStyle}>
            <EmailSentAlertComponent
              resetFlag={isRepeatSuccess}
              onAnimationEnd={handleEmailSendingOver}
            />
          </div>
        )}
      </div>
    </form>
  );
};
